import { Injectable } from "@angular/core";
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from "rxjs";
import { AuthService } from "src/app/_services/auth.service";
import { AuthenticationResponseDto } from "src/app/_models/auth/authentication-response.dto";
import { SnackbarService } from "src/app/_services/snackbar.service";
import { MessageTypeEnum } from "src/app/_enums/message-type.enum";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private refreshTokenInProgress = false;
    private refreshTokenSubject = new BehaviorSubject<AuthenticationResponseDto | null>(null);

    constructor(
        private authService: AuthService,
        private snackbarService: SnackbarService,
    ) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (request.headers.has("interceptor_skip_header")) {
            const headers = request.headers.delete("interceptor_skip_header");
            return next.handle(request.clone({ headers }));
        }

        return this.handle(request, next);
    }

    handle(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        request = this.cloneRequestWithNewHeaders(request);

        return next.handle(request).pipe(
            catchError((errorResponse: HttpErrorResponse) => {
                if (errorResponse.status === 401 && errorResponse.error?.message === "Unauthorized") {
                    if (this.refreshTokenInProgress) {
                        return this.refreshTokenSubject.pipe(
                            filter(Boolean),
                            take(1),
                            switchMap(() => next.handle(this.cloneRequestWithNewHeaders(request))),
                        );
                    } else {
                        this.refreshTokenInProgress = true;
                        this.refreshTokenSubject.next(null);

                        return this.authService.refreshToken().pipe(
                            switchMap((response) =>
                                next.handle(this.cloneRequestWithNewHeaders(request)).pipe(
                                    finalize(() => {
                                        this.refreshTokenInProgress = false;
                                        this.refreshTokenSubject.next(response);
                                    }),
                                ),
                            ),
                            catchError((error) => {
                                this.authService.logout();

                                this.snackbarService.show({
                                    type: MessageTypeEnum.ERROR,
                                    message: "Something went wrong. Please log in again.",
                                });

                                return throwError(() => error);
                            }),
                        );
                    }
                }

                return throwError(() => errorResponse);
            }),
        );
    }

    private cloneRequestWithNewHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
        const { token, refreshToken } = this.authService.authHeaders;

        return request.clone({
            setHeaders: Object.entries({
                authorization: token ? `Bearer ${token}` : undefined,
                refreshToken: refreshToken ?? undefined,
            }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {}),
        });
    }
}
